import Styles from './SelectImageContent.module.scss'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { AI, LinkAngled, Upload5 } from 'blaze-ui/icons'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useStateRef } from 'hooks/useStateRef'
import { useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AiGeneration } from 'components/ImagePickerModal/AIGeneration/AiGeneration'
import { Stock } from './Providers/Stock/Stock'
import { Link } from './Providers/Link/Link'
import { BrandKitImages } from './components/BrandKitImages'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ConfirmImage } from './ConfirmImage'
import { useImages } from 'components/Wizards/SelectImages/ImagesProvider'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { ConfirmVideo } from './ConfirmVideo'
import { isVideo } from 'components/ImagePickerModal/utils'
import { StockImagesAndVideos } from './components/StockImagesAndVideos'
import { StockImages } from './components/StockImages'
import { StockVideos } from './components/StockVideos'
import { LibraryImages } from './components/LibraryImages'
import { SelectedImage } from 'types'

interface Props {
  onFinalizeImageSelection: () => void
  showConfirmMedia: boolean
  setShowConfirmMedia: (value: boolean) => void
  confirmImage?: boolean
  confirmVideo?: boolean
  loadedPercentage?: number
}

export const SelectImageContent = ({
  onFinalizeImageSelection,
  showConfirmMedia,
  setShowConfirmMedia,
  confirmImage,
  confirmVideo,
  loadedPercentage,
}: Props) => {
  const isBrandKitsEnabled = useFlipperFlag('brand_kits')
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)
  const imagePickerContext = useImagePickerContext()
  const { value: brandKitId } = useField<number>('brand_kit_id')[0]
  const { imageKeywords, isGenerating } = useImages()
  const { setFieldValue } = useFormikContext()
  const { selectedImages, setProvider, provider, onUploadStart, uploadImages, accept, setSelectedImages, isUploading } =
    imagePickerContext

  const withVideo = accept?.includes(ACCEPTED_VIDEO_MIME_TYPES)
  const onlyVideo =
    withVideo && !accept?.includes(ACCEPTED_IMAGE_MIME_TYPES) && !accept?.includes(ACCEPTED_IMAGE_MIME_TYPES_NO_SVG)

  const isSelectedVideo =
    selectedImages[0] &&
    isVideo({ url: selectedImages[0].source_url || selectedImages[0].url, type: selectedImages[0].mime_type })

  const handleOnChange = async (files: File[]) => {
    const fileUrl = URL.createObjectURL(files[0])
    let toast

    if (isVideo({ url: fileUrl, type: files[0].type })) {
      setSelectedImages([{ mime_type: files[0].type, url: fileUrl }])
      setShowConfirmMedia(true)
    } else {
      toast = showToastMessage('Uploading Image...', 'loading', undefined, 0)
    }

    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)
    toast?.()

    setSelectedImages([uploadedImages[0]])
    setFieldValue('initial_media_file', uploadedImages[0])
  }

  // if the uploaded media is a video that needs to be confirmed, show confirmation screen.
  // Otherwise finalize image selection (go the next step)
  const handleSelectUploadedMediaFile = (mediaFile?: SelectedImage) => {
    const isMediaFileVideo = mediaFile
      ? isVideo({ url: mediaFile.source_url, type: mediaFile.mime_type })
      : isSelectedVideo

    if (confirmImage || (confirmVideo && isMediaFileVideo)) {
      setShowConfirmMedia(true)
    } else {
      onFinalizeImageSelection()
    }
  }

  useEffect(() => {
    if (fileInputRef?.value && !isSelectedVideo) handleSelectUploadedMediaFile()
  }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

  if (showConfirmMedia && confirmVideo && isSelectedVideo) {
    return (
      <ConfirmVideo
        thumbnailUrl={
          selectedImages?.[0]?.preview_variant_url || selectedImages?.[0]?.source_url || selectedImages?.[0]?.url
        }
        type={selectedImages[0]?.mime_type}
        loadedPercentage={loadedPercentage}
      />
    )
  }

  if (showConfirmMedia && confirmImage) {
    return <ConfirmImage imageUrl={selectedImages?.[0]?.preview_url || selectedImages?.[0]?.url} />
  }

  if (provider === 'ai') {
    return <AiGeneration />
  }

  if (provider === 'stock') {
    return <Stock withVideo={withVideo} onlyVideo={onlyVideo} />
  }

  if (provider === 'link') {
    return <Link />
  }

  let StockMediaComponent = StockImages
  if (withVideo) {
    StockMediaComponent = StockImagesAndVideos
  }
  if (onlyVideo) {
    StockMediaComponent = StockVideos
  }

  const LocalImagesComponent = brandKitId && isBrandKitsEnabled ? BrandKitImages : LibraryImages

  return (
    <div className={Styles.selectImageContainer}>
      <div className={Styles.sections}>
        <StockMediaComponent isDisabled={isUploading} imageKeywords={imageKeywords} isLoading={isGenerating} />
        <LocalImagesComponent
          onSelectImage={(image) => setSelectedImages([image])}
          brandKitId={brandKitId}
          onSelectImageFromAll={handleSelectUploadedMediaFile}
          isDisabled={isUploading}
          onBrandKitChange={(brandKitId) => {
            setFieldValue('brand_kit_id', brandKitId)
          }}
        />
      </div>
      <div className={Styles.actions}>
        <Button
          frontIcon={Upload5}
          variant={isDesktopWizardsUpdatesOn ? 'ghost' : 'secondary'}
          isDisabled={isUploading}
          onPress={() => fileInputRef?.click()}
        >
          {withVideo ? 'Upload Media' : 'Upload Image'}
        </Button>
        {!onlyVideo && (
          <>
            <div className={Styles.divider} />
            <Button
              frontIcon={AI}
              variant={isDesktopWizardsUpdatesOn ? 'ghost' : 'secondary'}
              isDisabled={isUploading}
              onPress={() => setProvider('ai')}
            >
              Generate image from AI
            </Button>
          </>
        )}
        <div className={Styles.divider} />
        <Button
          frontIcon={LinkAngled}
          variant={isDesktopWizardsUpdatesOn ? 'ghost' : 'secondary'}
          isDisabled={isUploading}
          onPress={() => setProvider('link')}
        >
          {withVideo ? 'Get media from a link' : 'Get image from a link'}
        </Button>
      </div>

      <VisuallyHidden>
        <input
          type="file"
          accept={accept}
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            handleOnChange(files)
          }}
          ref={setFileInputRef}
          multiple={false}
          name="insert image"
          id="insert image"
          data-testid="file-input"
        />
      </VisuallyHidden>
    </div>
  )
}
